import { useMemo } from 'react';
import { HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL } from '@/data/constants/product';
import { useProducts } from '@/data/Content/Products';
import { useContractByUserContext } from '@/data/Content/ContractByUserContext';
import { DpIARecommendationResponse } from 'integration/generated/transactions/data-contracts';
export type {
	DpIARecommendationSizeMode,
	DpIARecommendationSizes,
	DpIARecommendationType,
} from 'integration/generated/transactions/data-contracts';
import {
	DpIARecommendationOptions,
	useGetDpIARecommendation,
} from 'integration/hooks/useGetDpIARecommendation';
import { RecomIARecommendationOptions, useGetRecomIARecommendation } from 'integration/hooks/useGetRecomIARecommendation';
import { ProductType } from '@/data/types/Product';
import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';

export type IARecommendationSource = 'dp' | 'recom';

export const useCatalogEntryIARecommendation = ({
	params,
	partNumbersExtractor,
	orderExtractor,
	filters,
	types,
	sizes,
	sizeMode,
	source = 'dp',
	seedPartNumbers,
	withoutIdentity,
}: {
	params: {
		partNumber?: string;
	};
	partNumbersExtractor: (data: DpIARecommendationResponse) => string[];
	orderExtractor?: (products: ProductType[], data: DpIARecommendationResponse) => ProductType[];
	filters?: Array<any>;
	source?: IARecommendationSource;
	seedPartNumbers?: string[];
} & DpIARecommendationOptions &
	Pick<RecomIARecommendationOptions, 'withoutIdentity'>) => {
	const options = { types, sizes, sizeMode };
	const dpQuery = useGetDpIARecommendation(
		source === 'dp' ? params?.partNumber : undefined,
		filters,
		options
	);
	const recomQuery = useGetRecomIARecommendation(
		source === 'recom' ? params?.partNumber : undefined,
		filters,
		{ ...options, partNumbers: seedPartNumbers, withoutIdentity }
	);
	const { data: dpIARecommendation, isLoading: isLoadingDpIARecommendation } =
		source === 'recom' ? recomQuery : dpQuery;

	const partNumbers = useMemo(
		() => (dpIARecommendation ? partNumbersExtractor(dpIARecommendation) : []),
		[dpIARecommendation, partNumbersExtractor]
	);

	const contract = useContractByUserContext();

	const { products: rawProducts, loading: isLoadingProducts } = useProducts({
		partNumber: partNumbers,
		contractId: contract,
		query: { profileName: HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL },
	});

	const { data: productsWithInfoElectronico, isLoading: loadingInfoElectronicos } =
		useProductsWithInfoElectronico(rawProducts);

	const products = useMemo(
		() =>
			orderExtractor && dpIARecommendation
				? orderExtractor(productsWithInfoElectronico, dpIARecommendation)
				: productsWithInfoElectronico,
		[orderExtractor, productsWithInfoElectronico, dpIARecommendation]
	);

	return {
		isLoadingDpIARecommendation,
		isLoadingProducts: loadingInfoElectronicos || isLoadingProducts,
		dpIARecommendation,
		products,
		partNumbers,
		contract,
	};
};
